/**
 * GLM用のリンク関数実装
 *
 * リンク関数は、線形予測子η（eta）と平均μ（mu）を結びつける：
 * η = g(μ)  ... link function
 * μ = g⁻¹(η) ... inverse link function
 */

import type { GLMLink } from '../../types/model.types';

export interface LinkFunction {
  /**
   * リンク関数: η = g(μ)
   * @param mu 平均値
   * @returns 線形予測子
   */
  link(mu: number): number;

  /**
   * 逆リンク関数: μ = g⁻¹(η)
   * @param eta 線形予測子
   * @returns 平均値
   */
  inverse(eta: number): number;

  /**
   * 逆リンク関数の導関数: dμ/dη
   * @param eta 線形予測子
   * @returns 導関数の値
   */
  derivative(eta: number): number;

  /**
   * μの有効範囲を検証
   * @param mu 平均値
   * @returns 有効ならtrue
   */
  validateMu(mu: number): boolean;
}

/**
 * Identity Link: η = μ
 * 主にGaussian familyで使用
 */
export const IdentityLink: LinkFunction = {
  link(mu: number): number {
    return mu;
  },

  inverse(eta: number): number {
    return eta;
  },

  derivative(_eta: number): number {
    return 1.0;
  },

  validateMu(_mu: number): boolean {
    return true; // 全ての実数が有効
  }
};

/**
 * Logit Link: η = log(μ/(1-μ))
 * 主にBinomial familyで使用
 */
export const LogitLink: LinkFunction = {
  link(mu: number): number {
    // 数値的安定性のため、極端な値を制限
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, Math.min(1 - epsilon, mu));
    return Math.log(muClamped / (1 - muClamped));
  },

  inverse(eta: number): number {
    // オーバーフロー対策
    if (eta > 20) return 1.0 - 1e-10;
    if (eta < -20) return 1e-10;

    const expEta = Math.exp(eta);
    return expEta / (1 + expEta);
  },

  derivative(eta: number): number {
    // dμ/dη = μ(1-μ) where μ = inverse(eta)
    const mu = this.inverse(eta);
    return mu * (1 - mu);
  },

  validateMu(mu: number): boolean {
    return mu > 0 && mu < 1;
  }
};

/**
 * Log Link: η = log(μ)
 * 主にPoisson familyで使用
 */
export const LogLink: LinkFunction = {
  link(mu: number): number {
    // μは正でなければならない
    const epsilon = 1e-10;
    return Math.log(Math.max(epsilon, mu));
  },

  inverse(eta: number): number {
    // オーバーフロー対策
    if (eta > 20) return Math.exp(20);
    if (eta < -20) return Math.exp(-20);

    return Math.exp(eta);
  },

  derivative(eta: number): number {
    // dμ/dη = exp(η) = μ
    return this.inverse(eta);
  },

  validateMu(mu: number): boolean {
    return mu > 0;
  }
};

/**
 * Inverse Link: η = 1/μ
 * 主にGamma familyで使用
 */
export const InverseLink: LinkFunction = {
  link(mu: number): number {
    const epsilon = 1e-10;
    return 1.0 / Math.max(epsilon, Math.abs(mu));
  },

  inverse(eta: number): number {
    const epsilon = 1e-10;
    return 1.0 / Math.max(epsilon, Math.abs(eta));
  },

  derivative(eta: number): number {
    // dμ/dη = -1/η²
    const epsilon = 1e-10;
    const etaClamped = Math.max(epsilon, Math.abs(eta));
    return -1.0 / (etaClamped * etaClamped);
  },

  validateMu(mu: number): boolean {
    return mu > 0;
  }
};

/**
 * Probit Link: η = Φ⁻¹(μ) where Φは標準正規分布の累積分布関数
 * 主にBinomial familyで使用（代替リンク）
 */
export const ProbitLink: LinkFunction = {
  link(mu: number): number {
    // 数値的安定性のため、極端な値を制限
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, Math.min(1 - epsilon, mu));

    // 標準正規分布の逆累積分布関数の近似
    return inverseNormalCDF(muClamped);
  },

  inverse(eta: number): number {
    // 標準正規分布の累積分布関数
    return normalCDF(eta);
  },

  derivative(eta: number): number {
    // dμ/dη = φ(η) where φは標準正規分布の確率密度関数
    return normalPDF(eta);
  },

  validateMu(mu: number): boolean {
    return mu > 0 && mu < 1;
  }
};

/**
 * 標準正規分布の累積分布関数（CDF）
 */
function normalCDF(x: number): number {
  // 誤差関数を用いた近似
  return 0.5 * (1.0 + erf(x / Math.sqrt(2)));
}

/**
 * 標準正規分布の確率密度関数（PDF）
 */
function normalPDF(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

/**
 * 標準正規分布の逆累積分布関数（近似）
 * Acklam's algorithmの簡易版
 */
function inverseNormalCDF(p: number): number {
  if (p <= 0 || p >= 1) {
    throw new Error('Probability must be between 0 and 1');
  }

  // 中心部の近似
  const a1 = -3.969683028665376e+01;
  const a2 =  2.209460984245205e+02;
  const a3 = -2.759285104469687e+02;
  const a4 =  1.383577518672690e+02;
  const a5 = -3.066479806614716e+01;
  const a6 =  2.506628277459239e+00;

  const b1 = -5.447609879822406e+01;
  const b2 =  1.615858368580409e+02;
  const b3 = -1.556989798598866e+02;
  const b4 =  6.680131188771972e+01;
  const b5 = -1.328068155288572e+01;

  const c1 = -7.784894002430293e-03;
  const c2 = -3.223964580411365e-01;
  const c3 = -2.400758277161838e+00;
  const c4 = -2.549732539343734e+00;
  const c5 =  4.374664141464968e+00;
  const c6 =  2.938163982698783e+00;

  const d1 =  7.784695709041462e-03;
  const d2 =  3.224671290700398e-01;
  const d3 =  2.445134137142996e+00;
  const d4 =  3.754408661907416e+00;

  const pLow = 0.02425;
  const pHigh = 1 - pLow;

  if (p < pLow) {
    // 下側裾部
    const q = Math.sqrt(-2 * Math.log(p));
    return (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
           ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  } else if (p <= pHigh) {
    // 中心部
    const q = p - 0.5;
    const r = q * q;
    return (((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q /
           (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1);
  } else {
    // 上側裾部
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
            ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  }
}

/**
 * 誤差関数（Error Function）の近似
 */
function erf(x: number): number {
  // Abramowitz and Stegunの近似
  const a1 =  0.254829592;
  const a2 = -0.284496736;
  const a3 =  1.421413741;
  const a4 = -1.453152027;
  const a5 =  1.061405429;
  const p  =  0.3275911;

  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x);

  const t = 1.0 / (1.0 + p * absX);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);

  return sign * y;
}

/**
 * GLMリンク関数のレジストリ
 */
export const LinkFunctions: Record<GLMLink, LinkFunction> = {
  identity: IdentityLink,
  logit: LogitLink,
  log: LogLink,
  inverse: InverseLink,
  probit: ProbitLink
};

/**
 * リンク関数を取得
 */
export function getLinkFunction(link: GLMLink): LinkFunction {
  return LinkFunctions[link];
}

/**
 * 分布ファミリーに対するデフォルトリンク関数
 */
export const DefaultLinks: Record<string, GLMLink> = {
  gaussian: 'identity',
  binomial: 'logit',
  poisson: 'log',
  gamma: 'inverse'
};
